import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, apiFetch, configureApi } from '@/lib/api';

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'OPERATOR';

export interface AuthenticatedUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthResponse extends AuthTokens {
  user: AuthenticatedUser;
}

interface AuthState {
  user: AuthenticatedUser | null;
  tokens: AuthTokens | null;
  initializing: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const STORAGE_KEY = 'simpleadm.refreshToken';

const AuthContext = createContext<AuthContextValue | null>(null);

function storeRefresh(token: string | null) {
  if (token) localStorage.setItem(STORAGE_KEY, token);
  else localStorage.removeItem(STORAGE_KEY);
}

function loadRefresh(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: null,
    initializing: true,
  });
  // Ref pra leituras síncronas do configureApi
  const tokensRef = useRef<AuthTokens | null>(null);

  const setTokens = useCallback((tokens: AuthTokens | null, user: AuthenticatedUser | null) => {
    tokensRef.current = tokens;
    storeRefresh(tokens?.refreshToken ?? null);
    setState((s) => ({ ...s, tokens, user, initializing: false }));
  }, []);

  const performRefresh = useCallback(async (): Promise<AuthTokens | null> => {
    const refreshToken = loadRefresh();
    if (!refreshToken) {
      setTokens(null, null);
      return null;
    }
    try {
      const res = await apiFetch<AuthResponse>('/auth/refresh', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ refreshToken }),
      });
      setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken }, res.user);
      return { accessToken: res.accessToken, refreshToken: res.refreshToken };
    } catch {
      setTokens(null, null);
      return null;
    }
  }, [setTokens]);

  // Configura o client api com getters/refresh
  useEffect(() => {
    configureApi({
      getTokens: () => tokensRef.current,
      refresh: performRefresh,
    });
  }, [performRefresh]);

  // Tenta reaproveitar refresh token salvo ao montar
  useEffect(() => {
    const stored = loadRefresh();
    if (!stored) {
      setState((s) => ({ ...s, initializing: false }));
      return;
    }
    void performRefresh();
  }, [performRefresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiFetch<AuthResponse>('/auth/login', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ email, password }),
      });
      setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken }, res.user);
    },
    [setTokens],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokensRef.current?.refreshToken ?? loadRefresh();
    if (refreshToken) {
      try {
        await apiFetch('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        });
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) {
          // silenciamos erros — local logout sempre prossegue
          console.warn('logout falhou:', err);
        }
      }
    }
    setTokens(null, null);
  }, [setTokens]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
